import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, BellRing, Check, Inbox, LogOut, Mail, MessageCircle, Pencil, Plus, Radar, Send, Trash2, X } from "lucide-react";
import {
  fetchEvents,
  verifyAdmin,
  adminCreateEvent,
  adminUpdateEvent,
  adminDeleteEvent,
  adminSubmissions,
  adminScrapeLogs,
  adminReminders,
  adminDeleteReminder,
  adminDeleteSubmission,
  adminContactMessages,
  adminDeleteContact,
  adminApproveSubmission,
  adminRejectSubmission,
  getTwilioSettings,
  saveTwilioSettings,
  testTwilio,
} from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { LogoMark } from "@/components/LogoMark";
import { Footer } from "@/components/Footer";

const inputCls =
  "w-full rounded-xl border border-white/15 bg-black/50 px-4 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-volt focus:outline-none";

const EMPTY = {
  name: "",
  artist: "",
  category: "festival",
  genre: "",
  city: "",
  venue: "",
  event_date: "",
  event_end: "",
  sale_start: "",
  status_note: "",
  ticket_url: "",
  image_url: "",
  source_url: "",
  auto_update: false,
  featured: false,
};

const toLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
const fromLocal = (v) => (v ? new Date(v).toISOString() : null);
const orNull = (v) => (v && v.trim() ? v.trim() : null);

export default function AdminPage() {
  const [key, setKey] = useState(localStorage.getItem("wkv_admin_key") || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(false);
  const [events, setEvents] = useState([]);
  const [subs, setSubs] = useState([]);
  const [scrape, setScrape] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [twilio, setTwilio] = useState({
    enabled: false,
    account_sid: "",
    whatsapp_from: "",
    content_sid: "",
    auth_token: "",
    auth_token_set: false,
  });
  const [testNumber, setTestNumber] = useState("");
  const [twilioBusy, setTwilioBusy] = useState(false);

  const load = useCallback(async (k) => {
    const [ev, sb, sg, rm, tw, cm] = await Promise.all([
      fetchEvents(),
      adminSubmissions(k),
      adminScrapeLogs(k),
      adminReminders(k),
      getTwilioSettings(k),
      adminContactMessages(k),
    ]);
    setEvents(ev);
    setSubs(sb);
    setScrape(sg);
    setReminders(rm);
    setContacts(cm);
    setTwilio((t) => ({ ...t, ...tw, auth_token: "" }));
  }, []);

  useEffect(() => {
    if (key) {
      verifyAdmin(key)
        .then(() => {
          setAuthed(true);
          load(key).catch(() => {});
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setChecking(true);
    try {
      await verifyAdmin(key);
      localStorage.setItem("wkv_admin_key", key);
      setAuthed(true);
      await load(key);
    } catch {
      toast.error("Ongeldige admin-sleutel");
    } finally {
      setChecking(false);
    }
  };

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name,
      artist: orNull(form.artist),
      category: form.category,
      genre: orNull(form.genre),
      city: form.city,
      venue: form.venue,
      event_date: fromLocal(form.event_date),
      event_end: fromLocal(form.event_end),
      sale_start: fromLocal(form.sale_start),
      status_note: orNull(form.status_note),
      ticket_url: orNull(form.ticket_url),
      image_url: orNull(form.image_url),
      source_url: orNull(form.source_url),
      auto_update: !!form.auto_update,
      featured: !!form.featured,
    };
    try {
      if (editing) {
        await adminUpdateEvent(key, editing, payload);
        toast.success("Event bijgewerkt");
      } else {
        await adminCreateEvent(key, payload);
        toast.success("Event toegevoegd");
      }
      setForm(EMPTY);
      setEditing(null);
      await load(key);
    } catch {
      toast.error("Opslaan mislukt");
    }
  };

  const startEdit = (ev) => {
    setEditing(ev.id);
    setForm({
      name: ev.name,
      artist: ev.artist || "",
      category: ev.category,
      genre: ev.genre || "",
      city: ev.city,
      venue: ev.venue,
      event_date: toLocal(ev.event_date),
      event_end: toLocal(ev.event_end),
      sale_start: toLocal(ev.sale_start),
      status_note: ev.status_note || "",
      ticket_url: ev.ticket_url || "",
      image_url: ev.image_url || "",
      source_url: ev.source_url || "",
      auto_update: !!ev.auto_update,
      featured: !!ev.featured,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (ev) => {
    if (!window.confirm(`Verwijder "${ev.name}"?`)) return;
    try {
      await adminDeleteEvent(key, ev.id);
      toast.success("Event verwijderd");
      await load(key);
    } catch {
      toast.error("Verwijderen mislukt");
    }
  };

  const removeReminder = async (r) => {
    if (!window.confirm(`Verwijder aanmelding van ${r.email || r.phone}?`)) return;
    try {
      await adminDeleteReminder(key, r.id);
      toast.success("Aanmelding verwijderd");
      await load(key);
    } catch {
      toast.error("Verwijderen mislukt");
    }
  };

  const approveSubmission = async (s) => {
    try {
      await adminApproveSubmission(key, s.id);
      toast.success(`"${s.event_name}" staat live op de site`, {
        description: "Controleer zonodig de details (foto, links) in de Events-lijst.",
      });
      await load(key);
    } catch {
      toast.error("Publiceren mislukt");
    }
  };

  const rejectSubmission = async (s) => {
    if (!window.confirm(`Aanmelding "${s.event_name}" afwijzen?`)) return;
    try {
      await adminRejectSubmission(key, s.id);
      toast.success("Aanmelding afgewezen");
      await load(key);
    } catch {
      toast.error("Afwijzen mislukt");
    }
  };

  const removeSubmission = async (s) => {
    if (!window.confirm(`Verwijder aanmelding "${s.event_name}"?`)) return;
    try {
      await adminDeleteSubmission(key, s.id);
      toast.success("Aanmelding verwijderd");
      await load(key);
    } catch {
      toast.error("Verwijderen mislukt");
    }
  };

  const removeContact = async (c) => {
    if (!window.confirm(`Verwijder bericht van ${c.name}?`)) return;
    try {
      await adminDeleteContact(key, c.id);
      toast.success("Bericht verwijderd");
      await load(key);
    } catch {
      toast.error("Verwijderen mislukt");
    }
  };

  const saveTwilio = async (e) => {
    e.preventDefault();
    setTwilioBusy(true);
    try {
      await saveTwilioSettings(key, {
        enabled: twilio.enabled,
        account_sid: twilio.account_sid,
        whatsapp_from: twilio.whatsapp_from,
        content_sid: twilio.content_sid || null,
        auth_token: twilio.auth_token,
      });
      toast.success("Twilio-instellingen opgeslagen");
      await load(key);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setTwilioBusy(false);
    }
  };

  const sendTest = async () => {
    setTwilioBusy(true);
    try {
      await testTwilio(key, testNumber);
      toast.success("Testbericht verzonden", { description: "Controleer WhatsApp op het testnummer." });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Testverzending mislukt");
    } finally {
      setTwilioBusy(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("wkv_admin_key");
    setAuthed(false);
    setKey("");
  };

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-obsidian px-4 text-white">
        <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-panel p-8" data-testid="admin-login-card">
          <div className="flex items-center gap-2.5">
            <LogoMark className="h-8 w-8" />
            <span className="font-display font-bold">Admin</span>
          </div>
          <p className="mt-2 text-sm text-slate-400">Log in met je admin-sleutel om events te beheren.</p>
          <form onSubmit={login} className="mt-6 space-y-3">
            <input
              type="password"
              required
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="Admin-sleutel"
              data-testid="admin-key-input"
              className={inputCls}
            />
            <button
              type="submit"
              disabled={checking}
              data-testid="admin-login-button"
              className="w-full rounded-full bg-volt py-2.5 text-sm font-bold text-black transition-transform hover:scale-[1.02] disabled:opacity-50"
            >
              {checking ? "Controleren..." : "Inloggen"}
            </button>
          </form>
          <Link to="/" className="mt-4 inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300">
            <ArrowLeft className="h-3 w-3" /> Terug naar de site
          </Link>
        </div>
      </div>
    );
  }

  const twilioConfigured = twilio.enabled && twilio.account_sid && twilio.auth_token_set && twilio.whatsapp_from;

  return (
    <div className="min-h-screen bg-obsidian text-white" data-testid="admin-dashboard">
      <div className="border-b border-white/10 bg-black/60">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-2.5">
            <LogoMark className="h-7 w-7" />
            <span className="font-display font-bold">Eventbeheer</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white" data-testid="admin-back-link">
              <ArrowLeft className="h-3.5 w-3.5" /> Site
            </Link>
            <button onClick={logout} className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-blaze" data-testid="admin-logout-button">
              <LogOut className="h-3.5 w-3.5" /> Uitloggen
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-panel p-6" data-testid="admin-event-form">
          <h2 className="flex items-center gap-2 font-display text-lg font-bold">
            <Plus className="h-4 w-4 text-volt" />
            {editing ? "Event bewerken" : "Nieuw event"}
          </h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <input required value={form.name} onChange={set("name")} placeholder="Naam event *" data-testid="admin-event-name-input" className={inputCls} />
            <input value={form.artist} onChange={set("artist")} placeholder="Artiest (optioneel)" data-testid="admin-event-artist-input" className={inputCls} />
            <select value={form.category} onChange={set("category")} data-testid="admin-event-category-select" className={inputCls}>
              <option value="festival">Festival</option>
              <option value="concert">Concert</option>
              <option value="sport">Sport & Fit</option>
              <option value="club">Rave & Indoor</option>
              <option value="voetbal">Voetbal</option>
            </select>
            <input value={form.genre} onChange={set("genre")} placeholder="Genre-label (bv. Techno, Hardstyle)" data-testid="admin-event-genre-input" className={inputCls} />
            <input required value={form.city} onChange={set("city")} placeholder="Stad *" data-testid="admin-event-city-input" className={inputCls} />
            <input required value={form.venue} onChange={set("venue")} placeholder="Locatie/venue *" data-testid="admin-event-venue-input" className={inputCls} />
            <label className="block">
              <span className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Eventdatum (start)</span>
              <input type="datetime-local" value={form.event_date} onChange={set("event_date")} data-testid="admin-event-date-input" className={inputCls} />
            </label>
            <label className="block">
              <span className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Eventdatum (eind, optioneel)</span>
              <input type="datetime-local" value={form.event_end} onChange={set("event_end")} data-testid="admin-event-end-input" className={inputCls} />
            </label>
            <label className="block">
              <span className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Start kaartverkoop (leeg = verwacht)</span>
              <input type="datetime-local" value={form.sale_start} onChange={set("sale_start")} data-testid="admin-event-sale-input" className={inputCls} />
            </label>
            <input value={form.status_note} onChange={set("status_note")} placeholder='Statusnotitie (bv. "Pre-sale verwacht in nov")' data-testid="admin-event-note-input" className={inputCls} />
            <input value={form.ticket_url} onChange={set("ticket_url")} placeholder="Ticketlink (https://...)" data-testid="admin-event-ticket-input" className={inputCls} />
            <input value={form.source_url} onChange={set("source_url")} placeholder="Bron-URL voor scraper (https://...)" data-testid="admin-event-source-input" className={inputCls} />
          </div>
          <input value={form.image_url} onChange={set("image_url")} placeholder="Afbeelding URL (https://...)" data-testid="admin-event-image-input" className={`${inputCls} mt-3`} />
          <label className="mt-3 flex items-center gap-2 text-xs text-slate-400">
            <input type="checkbox" checked={form.auto_update} onChange={set("auto_update")} data-testid="admin-event-autoupdate-checkbox" className="h-4 w-4 accent-[#00E676]" />
            Scraper mag de verkoopdatum van dit event automatisch verversen
          </label>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button type="submit" data-testid="admin-add-event-button" className="rounded-full bg-volt px-6 py-2.5 text-sm font-bold text-black transition-transform hover:scale-[1.03]">
              {editing ? "Opslaan" : "Toevoegen"}
            </button>
            {editing && (
              <button
                type="button"
                onClick={() => {
                  setEditing(null);
                  setForm(EMPTY);
                }}
                data-testid="admin-cancel-edit-button"
                className="rounded-full border border-white/15 px-6 py-2.5 text-sm font-semibold text-slate-300"
              >
                Annuleren
              </button>
            )}
          </div>
        </form>

        <div className="mt-10">
          <h2 className="font-display text-lg font-bold">Events ({events.length})</h2>
          <div className="mt-4 space-y-2" data-testid="admin-events-list">
            {events.map((ev) => (
              <div key={ev.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-white/[0.08] bg-panel px-4 py-3" data-testid={`admin-event-row-${ev.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{ev.name}</p>
                  <p className="font-mono text-[11px] text-slate-500">
                    {ev.genre || ev.category} · {ev.city} · verkoop: {ev.sale_start ? fmtDate(ev.sale_start, true) : "nnb"}
                    {ev.status_note ? ` · ${ev.status_note}` : ""}
                    {ev.auto_update ? " · auto" : ""}
                  </p>
                </div>
                <span className={`rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest ${ev.status === "live" ? "bg-[#FF3B30]/15 text-[#FF6B60]" : ev.status === "binnenkort" ? "bg-volt/10 text-volt" : "bg-white/5 text-slate-400"}`}>
                  {ev.status}
                </span>
                <button onClick={() => startEdit(ev)} data-testid={`admin-edit-event-${ev.id}`} className="rounded-full border border-white/15 p-2 text-slate-300 transition-colors hover:border-volt/60 hover:text-volt">
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => remove(ev)} data-testid={`admin-delete-event-${ev.id}`} className="rounded-full border border-white/15 p-2 text-slate-300 transition-colors hover:border-[#FF3B30]/60 hover:text-[#FF3B30]">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10">
          <h2 className="flex items-center gap-2 font-display text-lg font-bold">
            <BellRing className="h-4 w-4 text-volt" /> Herinneringen & alerts ({reminders.length})
          </h2>
          <p className="mt-1 text-xs text-slate-500">
            {twilioConfigured
              ? "E-mail- én WhatsApp-herinneringen worden automatisch verstuurd zodra de verkoop live gaat."
              : "E-mailherinneringen gaan automatisch uit. WhatsApp-nummers worden verzameld — activeer verzending hieronder bij Twilio WhatsApp."}
          </p>
          <div className="mt-4 space-y-2" data-testid="admin-reminders-list">
            {reminders.length === 0 && (
              <p className="rounded-xl border border-dashed border-white/15 py-8 text-center text-sm text-slate-500">
                Nog geen aanmeldingen voor herinneringen.
              </p>
            )}
            {reminders.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-white/[0.08] bg-panel px-4 py-3" data-testid={`admin-reminder-row-${r.id}`}>
                <span className={`rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest ${r.channel === "whatsapp" ? "bg-[#25D366]/15 text-[#25D366]" : "bg-volt/10 text-volt"}`}>
                  {r.channel === "whatsapp" ? "WhatsApp" : "E-mail"}
                </span>
                <p className="min-w-0 flex-1 truncate text-sm font-semibold">{r.email || r.phone}</p>
                <p className="font-mono text-[11px] text-slate-500">{r.event_name || "Alle events"} · {fmtDate(r.created_at, true)}</p>
                <span className={`rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest ${r.unsubscribed ? "bg-[#FF3B30]/15 text-[#FF6B60]" : r.event_name ? (r.notified ? "bg-volt/10 text-volt" : "bg-white/5 text-slate-400") : "bg-[#00E5FF]/10 text-[#00E5FF]"}`}>
                  {r.unsubscribed ? "uitgeschreven" : r.event_name ? (r.notified ? "verstuurd" : "wacht") : "doorlopend"}
                </span>
                <button onClick={() => removeReminder(r)} data-testid={`admin-delete-reminder-${r.id}`} className="rounded-full border border-white/15 p-2 text-slate-300 transition-colors hover:border-[#FF3B30]/60 hover:text-[#FF3B30]">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10">
          <h2 className="flex items-center gap-2 font-display text-lg font-bold">
            <MessageCircle className="h-4 w-4 text-[#25D366]" /> Twilio WhatsApp
          </h2>
          <form onSubmit={saveTwilio} className="mt-4 rounded-2xl border border-white/10 bg-panel p-6" data-testid="twilio-settings-form">
            <div className="flex items-center gap-3">
              <span data-testid="twilio-status" className={`rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest ${twilioConfigured ? "bg-[#25D366]/15 text-[#25D366]" : "bg-white/5 text-slate-400"}`}>
                {twilioConfigured ? "Actief" : "Niet geconfigureerd"}
              </span>
              <label className="flex items-center gap-2 text-xs text-slate-400">
                <input
                  type="checkbox"
                  checked={twilio.enabled}
                  onChange={(e) => setTwilio((t) => ({ ...t, enabled: e.target.checked }))}
                  data-testid="twilio-enabled-toggle"
                  className="h-4 w-4 accent-[#25D366]"
                />
                WhatsApp-verzending inschakelen
              </label>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <input
                value={twilio.account_sid}
                onChange={(e) => setTwilio((t) => ({ ...t, account_sid: e.target.value.trim() }))}
                placeholder="Account SID (AC...)"
                data-testid="twilio-sid-input"
                className={inputCls}
              />
              <input
                type="password"
                value={twilio.auth_token}
                onChange={(e) => setTwilio((t) => ({ ...t, auth_token: e.target.value }))}
                placeholder={twilio.auth_token_set ? "Auth Token ingesteld — leeg = behouden" : "Auth Token"}
                data-testid="twilio-token-input"
                className={inputCls}
              />
              <input
                value={twilio.whatsapp_from}
                onChange={(e) => setTwilio((t) => ({ ...t, whatsapp_from: e.target.value.trim() }))}
                placeholder="Verzendnummer (whatsapp:+14155238886)"
                data-testid="twilio-from-input"
                className={inputCls}
              />
              <input
                value={twilio.content_sid}
                onChange={(e) => setTwilio((t) => ({ ...t, content_sid: e.target.value.trim() }))}
                placeholder="Template SID HX... (optioneel, productie)"
                data-testid="twilio-content-sid-input"
                className={inputCls}
              />
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button type="submit" disabled={twilioBusy} data-testid="twilio-save-button" className="rounded-full bg-volt px-6 py-2.5 text-sm font-bold text-black transition-transform hover:scale-[1.03] disabled:opacity-50">
                {twilioBusy ? "Bezig..." : "Opslaan"}
              </button>
              <input
                value={testNumber}
                onChange={(e) => setTestNumber(e.target.value)}
                placeholder="Testnummer (+316...)"
                data-testid="twilio-test-number-input"
                className={`${inputCls} max-w-56`}
              />
              <button type="button" onClick={sendTest} disabled={twilioBusy || !testNumber} data-testid="twilio-test-button" className="inline-flex items-center gap-1.5 rounded-full border border-[#25D366]/40 px-5 py-2.5 text-sm font-semibold text-[#25D366] transition-colors hover:bg-[#25D366]/10 disabled:opacity-40">
                <Send className="h-3.5 w-3.5" /> Test verzenden
              </button>
            </div>
            <p className="mt-3 text-[11px] leading-relaxed text-slate-500">
              Testen kan gratis via de Twilio Sandbox: activeer de sandbox in de Twilio Console en stuur de join-code vanaf het testnummer naar het sandbox-nummer. Voor productie heb je een geverifieerd WhatsApp Business-nummer en een goedgekeurd template (HX...) nodig.
            </p>
          </form>
        </div>

        <div className="mt-10">
          <h2 className="flex items-center gap-2 font-display text-lg font-bold">
            <Radar className="h-4 w-4 text-volt" /> Scraper-runs
          </h2>
          <div className="mt-4 space-y-2" data-testid="admin-scrape-runs">
            {!scrape || scrape.runs.length === 0 ? (
              <p className="rounded-xl border border-dashed border-white/15 py-8 text-center text-sm text-slate-500">
                Nog geen scrape-runs. De dagelijkse cron draait om 08:00 (NL).
              </p>
            ) : (
              scrape.runs.slice(0, 5).map((r) => (
                <div key={r.run_id} className="flex flex-wrap items-center gap-3 rounded-xl border border-white/[0.08] bg-panel px-4 py-3">
                  <p className="font-mono text-[11px] text-slate-400">{fmtDate(r.started_at, true)}</p>
                  <p className="font-mono text-[11px] text-slate-500">
                    {r.checked ?? "…"} gecheckt · {r.updated ?? "…"} bijgewerkt
                  </p>
                  <span className={`rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest ${r.finished_at ? "bg-volt/10 text-volt" : "bg-blaze/10 text-blaze"}`}>
                    {r.finished_at ? "klaar" : "bezig"}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="mt-10 pb-16">
          <h2 className="flex items-center gap-2 font-display text-lg font-bold">
            <Inbox className="h-4 w-4 text-blaze" /> Aangevraagde Events ({subs.length})
          </h2>
          <p className="mt-1 text-xs text-slate-500">
            Ingezonden via "Event aanmelden". Goedkeuren zet het event meteen live op de site én in de dynamische sitemap.
          </p>
          <div className="mt-4 space-y-2" data-testid="admin-submissions-list">
            {subs.length === 0 && (
              <p className="rounded-xl border border-dashed border-white/15 py-8 text-center text-sm text-slate-500">
                Nog geen aanmeldingen.
              </p>
            )}
            {subs.map((s) => {
              const st = s.status || "pending";
              return (
                <div key={s.id} className="flex flex-wrap items-start gap-3 rounded-xl border border-white/[0.08] bg-panel px-4 py-3" data-testid={`admin-submission-row-${s.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">{s.event_name} <span className="font-mono text-[10px] uppercase text-slate-500">· {s.category}{s.city ? ` · ${s.city}` : ""}</span></p>
                    <p className="mt-1 text-xs text-slate-400">
                      {s.organizer_name} — {s.email}
                      {s.website ? ` · ${s.website}` : ""}
                    </p>
                    {s.message && <p className="mt-1 text-xs text-slate-500">{s.message}</p>}
                  </div>
                  <span className={`rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest ${st === "approved" ? "bg-volt/10 text-volt" : st === "rejected" ? "bg-[#FF3B30]/15 text-[#FF6B60]" : "bg-blaze/15 text-blaze"}`} data-testid={`submission-status-${s.id}`}>
                    {st === "approved" ? "Gepubliceerd" : st === "rejected" ? "Afgewezen" : "In afwachting"}
                  </span>
                  {st === "pending" && (
                    <>
                      <button
                        onClick={() => approveSubmission(s)}
                        data-testid={`admin-approve-submission-${s.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-volt px-4 py-2 text-xs font-bold text-black transition-transform hover:scale-[1.04]"
                      >
                        <Check className="h-3.5 w-3.5" /> Goedkeuren & publiceren
                      </button>
                      <button
                        onClick={() => rejectSubmission(s)}
                        data-testid={`admin-reject-submission-${s.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#FF3B30]/40 px-4 py-2 text-xs font-semibold text-[#FF6B60] transition-colors hover:bg-[#FF3B30]/10"
                      >
                        <X className="h-3.5 w-3.5" /> Afwijzen
                      </button>
                    </>
                  )}
                  <button onClick={() => removeSubmission(s)} data-testid={`admin-delete-submission-${s.id}`} className="rounded-full border border-white/15 p-2 text-slate-300 transition-colors hover:border-[#FF3B30]/60 hover:text-[#FF3B30]">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-10 pb-16">
          <h2 className="flex items-center gap-2 font-display text-lg font-bold">
            <Mail className="h-4 w-4 text-blaze" /> Contactberichten ({contacts.length})
          </h2>
          <div className="mt-4 space-y-2" data-testid="admin-contacts-list">
            {contacts.length === 0 && (
              <p className="rounded-xl border border-dashed border-white/15 py-8 text-center text-sm text-slate-500">
                Nog geen contactberichten.
              </p>
            )}
            {contacts.map((c) => (
              <div key={c.id} className="flex flex-wrap items-start gap-3 rounded-xl border border-white/[0.08] bg-panel px-4 py-3" data-testid={`admin-contact-row-${c.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">{c.name} <span className="font-mono text-[11px] text-slate-500">· {c.email} · {fmtDate(c.created_at, true)}</span></p>
                  <p className="mt-1 text-xs text-slate-400 whitespace-pre-wrap">{c.message}</p>
                </div>
                <button onClick={() => removeContact(c)} data-testid={`admin-delete-contact-${c.id}`} className="rounded-full border border-white/15 p-2 text-slate-300 transition-colors hover:border-[#FF3B30]/60 hover:text-[#FF3B30]">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
